/**
 * Mini-SVG-Icons für Terrain-Subtypes.
 *
 * Render-Priorität:
 *   1. icon_svg_json aus DB (Registry, KI-generiert per Bauplan)
 *   2. SCENES (statische R103.38-Subtypes, deutsche Originale)
 *   3. FALLBACK (generischer Berg + Pilot + Segler)
 *
 * Stil:
 *   viewBox 48x32 (querformat), currentColor stroke 1.4px
 *   Pilot = 2-Strich-Figur mit Kopf-Punkt ~6px hoch
 *   Glider = stilisiertes Segelflugmodell mit Rumpf + Tragfläche + T-Leitwerk
 *   Terrain = Höhenprofil-Pfad pro Subtype
 *
 * Verwendung:
 *   <TerrainSubtypeIcon subtype="FLATLAND_GENTLE" size={20} />
 *   <TerrainSubtypeIcon iconSvgJson={registry.icon_svg_json} size={20} />
 *
 * v1.0 Sprint 7.7 (2026-06-12): Glider als T-Leitwerk-Modell, plus dynamische
 * Loading aus DB für KI-generierte Bauplan-Icons je Subtype.
 */

import { ReactNode } from 'react'

// ────────────────────────────────────────────────────────────────────────
// Bauplan-Schema (KI-Output)
// ────────────────────────────────────────────────────────────────────────

/**
 * Bauplan-JSON für ein Subtype-Icon. Frontend rendert daraus deterministisch
 * via React-Helper. Kein roher SVG-String — verhindert XSS strukturell.
 */
export type SvgIconBlueprint = {
  version: 1
  /** SVG-path-data für Terrain-Linie. Coords im viewBox 48×32. */
  terrain_path: string
  /** Pilot-Figur Position. Optional weglassen falls Icon ohne Pilot. */
  pilot?: { x: number; y: number }
  /** Segler-Glider Position + Rotation + Skalierung. */
  glider?: { x: number; y: number; rotate?: number; scale?: number }
  /** Optional: Thermik-Punktespur am Boden. */
  thermal?: { cx: number; cy: number }
  /** Optional: Aufwind-Pfeil. */
  lift?: { x: number; y: number; h?: number }
}

// ────────────────────────────────────────────────────────────────────────
// Reusable SVG-Bausteine
// ────────────────────────────────────────────────────────────────────────

// Pilot-Figur — Kopf-Kreis + Körper-Strich + 2 Beine. ~6px hoch.
function Pilot({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <circle cx="0" cy="-4" r="1.2" fill="currentColor" stroke="none" />
      <line x1="0" y1="-3" x2="0" y2="0" />
      <line x1="0" y1="0" x2="-1.5" y2="2.5" />
      <line x1="0" y1="0" x2="1.5" y2="2.5" />
    </g>
  )
}

// Segelflug-Modell mit T-Leitwerk.
//   Rumpf (waagrechter Strich) + Tragfläche (kurzer Querstrich vorne)
//   + Höhenleitwerk T (kleine Querlinie am Heck) + Seitenruder (Vertikal-Stub)
//   + Cockpit-Punkt vorne. Erkennbar als RC-Segelflugmodell.
function Glider({
  x,
  y,
  rotate = 0,
  scale = 1,
}: {
  x: number
  y: number
  rotate?: number
  scale?: number
}) {
  return (
    <g transform={`translate(${x},${y}) rotate(${rotate}) scale(${scale})`}>
      {/* Rumpf — von Nase (links) bis Heck (rechts) */}
      <line x1="-4" y1="0" x2="3" y2="0" />
      {/* Tragfläche — Querlinie etwas hinter der Nase (1/3 Rumpflänge) */}
      <line x1="-3.5" y1="-1.4" x2="-0.5" y2="-1.4" />
      {/* Tragflächen-Befestigung am Rumpf */}
      <line x1="-2" y1="-1.4" x2="-2" y2="0" />
      {/* Seitenruder — kurzer Vertikalstrich am Heck */}
      <line x1="2.8" y1="-1.8" x2="2.8" y2="0" />
      {/* Höhenleitwerk T — Querlinie oben am Seitenruder */}
      <line x1="1.8" y1="-1.8" x2="3.8" y2="-1.8" />
      {/* Cockpit-Punkt vorne */}
      <circle cx="-3.5" cy="0" r="0.55" fill="currentColor" stroke="none" />
    </g>
  )
}

// Thermik-Punktespur (4 Punkte deuten Kreisspur an).
function ThermalDots({ cx, cy }: { cx: number; cy: number }) {
  return (
    <g fill="currentColor" stroke="none" opacity="0.55">
      <circle cx={cx - 2} cy={cy + 1.5} r="0.55" />
      <circle cx={cx + 2} cy={cy + 0.5} r="0.55" />
      <circle cx={cx - 1} cy={cy - 1.5} r="0.55" />
      <circle cx={cx + 1.5} cy={cy - 2.5} r="0.55" />
    </g>
  )
}

// Aufwind-Pfeil (gestrichelt nach oben).
function LiftArrow({ x, y, h = 6 }: { x: number; y: number; h?: number }) {
  return (
    <g opacity="0.65">
      <line x1={x} y1={y} x2={x} y2={y - h} strokeDasharray="1 1" />
      <polyline points={`${x - 1},${y - h + 1.5} ${x},${y - h} ${x + 1},${y - h + 1.5}`} fill="none" />
    </g>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Statische SCENES (R103.38-Subtypes mit hand-getunten Bauplänen)
// ────────────────────────────────────────────────────────────────────────

const SCENES: Record<string, ReactNode> = {
  // ─── FLATLAND ───────────────────────────────────────────────────────
  FLATLAND_PURE: (
    <>
      <line x1="2" y1="26" x2="46" y2="26" />
      <Pilot x={36} y={26} />
      <Glider x={16} y={9} rotate={-10} />
      <ThermalDots cx={16} cy={13} />
    </>
  ),
  FLATLAND_GENTLE: (
    <>
      <path d="M 2 26 Q 10 22, 18 26 T 34 26 T 46 26" fill="none" />
      <Pilot x={24} y={24} />
      <Glider x={14} y={9} rotate={-8} />
    </>
  ),
  FLATLAND_VALLEY: (
    <>
      <path d="M 2 12 L 10 12 L 18 26 L 30 26 L 38 12 L 46 12" fill="none" />
      <Pilot x={24} y={26} />
      <Glider x={24} y={9} rotate={5} />
      <ThermalDots cx={24} cy={14} />
    </>
  ),
  FLATLAND_HILLY_NONALPINE: (
    <>
      <path d="M 2 26 Q 9 18, 16 26 Q 22 16, 28 26 Q 35 19, 42 26 L 46 26" fill="none" />
      <Pilot x={22} y={17.5} />
      <Glider x={32} y={10} rotate={-12} />
    </>
  ),

  // ─── SLOPE ──────────────────────────────────────────────────────────
  SLOPE_MIDRANGE: (
    <>
      <path d="M 2 12 L 22 12 L 34 22 L 46 26" fill="none" />
      <Pilot x={14} y={12} />
      <Glider x={28} y={9} rotate={-25} />
      <LiftArrow x={36} y={20} h={6} />
    </>
  ),
  SLOPE_ALPINE_HILLY: (
    <>
      <path d="M 2 26 L 10 16 L 14 22 L 22 10 L 26 18 L 34 12 L 40 22 L 46 26" fill="none" />
      <Pilot x={22} y={10} />
      <Glider x={32} y={6} rotate={-15} scale={0.9} />
      <LiftArrow x={26} y={20} h={5} />
    </>
  ),
  SLOPE_ALPINE_RIDGE: (
    <>
      <path d="M 2 28 L 22 6 L 26 6 L 46 28" fill="none" />
      <Pilot x={24} y={6} />
      <Glider x={36} y={13} rotate={-30} />
      <LiftArrow x={30} y={20} h={6} />
    </>
  ),
  SLOPE_CLIFF: (
    <>
      <path d="M 2 10 L 28 10 L 28 28 L 46 28" fill="none" />
      <Pilot x={26} y={10} />
      <Glider x={38} y={14} rotate={-40} scale={0.95} />
      <LiftArrow x={32} y={26} h={10} />
    </>
  ),

  // ─── Stephan-Custom-Subtype ─────────────────────────────────────────
  'Steiler Hang mit langer Kante': (
    <>
      <path d="M 2 8 L 22 8 L 44 28" fill="none" />
      <Pilot x={20} y={8} />
      <Glider x={32} y={12} rotate={-35} />
      <LiftArrow x={30} y={22} h={7} />
    </>
  ),
}

// Generischer Fallback: Berg + Pilot + Glider
const FALLBACK: ReactNode = (
  <>
    <path d="M 2 26 L 16 14 L 26 22 L 38 10 L 46 26" fill="none" />
    <Pilot x={36} y={10} />
    <Glider x={20} y={7} rotate={-8} />
  </>
)

// ────────────────────────────────────────────────────────────────────────
// Bauplan-Renderer (für DB-gespeicherte KI-Bauplane)
// ────────────────────────────────────────────────────────────────────────

function isValidBlueprint(bp: any): bp is SvgIconBlueprint {
  if (!bp || typeof bp !== 'object') return false
  if (bp.version !== 1) return false
  if (typeof bp.terrain_path !== 'string' || bp.terrain_path.length === 0) return false
  // Sanitization: Pfad darf nur SVG-path-Token enthalten (Buchstaben, Zahlen,
  // Komma, Punkt, Leerzeichen, Minus). Kein < oder anderes Tag-Risiko.
  if (!/^[MmLlHhVvCcSsQqTtAaZz0-9\s.,\-]+$/.test(bp.terrain_path)) return false
  return true
}

function renderBlueprint(bp: SvgIconBlueprint): ReactNode {
  return (
    <>
      <path d={bp.terrain_path} fill="none" />
      {bp.pilot && <Pilot x={bp.pilot.x} y={bp.pilot.y} />}
      {bp.glider && (
        <Glider
          x={bp.glider.x}
          y={bp.glider.y}
          rotate={bp.glider.rotate ?? 0}
          scale={bp.glider.scale ?? 1}
        />
      )}
      {bp.thermal && <ThermalDots cx={bp.thermal.cx} cy={bp.thermal.cy} />}
      {bp.lift && <LiftArrow x={bp.lift.x} y={bp.lift.y} h={bp.lift.h ?? 6} />}
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────
// Public Komponente
// ────────────────────────────────────────────────────────────────────────

type Props = {
  /** Subtype-Name (technischer Identifier). Fallback wenn iconSvgJson nicht da. */
  subtype?: string | null
  /** Bauplan aus DB (KI-generiert via terrain-subtype-svg-generator). */
  iconSvgJson?: SvgIconBlueprint | unknown | null
  size?: number
  className?: string
  title?: string
}

export function TerrainSubtypeIcon({ subtype, iconSvgJson, size = 18, className, title }: Props) {
  // Priorität 1: KI-Bauplan aus DB
  let scene: ReactNode | null = null
  if (iconSvgJson && isValidBlueprint(iconSvgJson)) {
    scene = renderBlueprint(iconSvgJson)
  }
  // Priorität 2: statisches SCENES für R103.38-Subtypes
  if (!scene) {
    const key = (subtype ?? '').trim()
    if (key && SCENES[key]) scene = SCENES[key]
  }
  // Priorität 3: Fallback
  if (!scene) scene = FALLBACK

  return (
    <svg
      viewBox="0 0 48 32"
      width={size * 1.5}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-label={title}
    >
      {title && <title>{title}</title>}
      {scene}
    </svg>
  )
}

/**
 * Hilfsfunktion: liefert den Klartext-Namen für einen Subtype.
 * Wird im UI verwendet wenn kein display_name aus der Registry verfügbar ist.
 */
export function defaultDisplayNameFor(subtype: string | null | undefined): string {
  if (!subtype) return ''
  const KNOWN: Record<string, string> = {
    FLATLAND_PURE: 'Reines Flachland',
    FLATLAND_GENTLE: 'Sanfte Wellen',
    FLATLAND_VALLEY: 'Tal-Boden',
    FLATLAND_HILLY_NONALPINE: 'Hügelig (nicht alpin)',
    SLOPE_MIDRANGE: 'Mittelgebirgs-Hang',
    SLOPE_ALPINE_HILLY: 'Alpiner Hügelhang',
    SLOPE_ALPINE_RIDGE: 'Alpine Kammlage',
    SLOPE_CLIFF: 'Klippe / Steilküste',
  }
  return KNOWN[subtype] || subtype
}
